import React, { useEffect, useMemo, useState } from 'react';
import { LoadingBounce, useDialogs } from '@michelin/acid-components';
import { useTranslation } from '@michelin/central-provider';
import { Checkbox, Grid } from '@material-ui/core';
import DataGrid, { Column, FilterRow, SearchPanel } from 'devextreme-react/data-grid';
import {
  cloneDeep,
} from 'lodash';
import { useContact, useContactList } from '../../../hooks/useContactQueries';
import useCustomer from '../../../hooks/useCustomer';
import {
  Contact,
  ContactListItem,
  ContactType,
  SPPContactTypeLevelsEnum,
  ContactTypeRole,
  ContactTypeRoleLevels,
  SPPContactTypeRoleAreasEnum,
  SPPContactTypeLevels,
  SPPContactTypeRoleAreas,
  SPPContactTypeServices,
} from '../../../Types';
import {
  GetLevelLabel, GetRoleAreaLabel, GetServiceLabel, hasMichelinContactTypes,
} from '../Utils';
import { getContactTypeMapKey, savePriorityLevels, saveReassignments } from './utils';
import { PriorityLevelsReassignment } from './PriorityLevelsReassignment';

interface PriorityLevelsManagementProps {
  contactId: string,
  contactLevel: string,
  stNumber: string,
  btNumber: string,
  countryCode: string,
  action: string,
  forceReassign: boolean,
  onSave: any,
  onChange: any,
}

function PriorityLevelsManagement(props:PriorityLevelsManagementProps) {
  const {
    contactId, contactLevel, stNumber, btNumber, countryCode, action, forceReassign, onSave, onChange,
  } = props;

  const { t } = useTranslation();
  const { confirmDialog } = useDialogs();

  const contactQuery = useContact({
    stNumber,
    contactId,
    defaultData: undefined,
    queryKey: 'priority-levels-management',
  });

  const contactListQuery = useContactList({
    stNumber, accountLevel: contactLevel, contactType: '', queryKey: 'priority-levels-management',
  });

  const customerQuery = useCustomer(stNumber);

  const [allPrimaryClicked, setAllPrimaryClicked] = useState<boolean>(false);
  const [allSecondaryClicked, setAllSecondaryClicked] = useState<boolean>(false);
  const [allPrimary, setAllPrimary] = useState<boolean>(false);
  const [allSecondary, setAllSecondary] = useState<boolean>(false);
  const [saving, setSaving] = useState<boolean>(false);
  const [reassigning, setReassigning] = useState<boolean>(false);
  const [hasMichelinContactTypesLevels, setHasMichelinContactTypesLevels] = useState<boolean>(false);
  const [contactTypes, setContactTypes] = useState<ContactType[]>([]);
  const [existingAssignmentsMap] = useState<Map<string, string>>(new Map<string, string>());
  const [currentlyAssignedContactsMap] = useState<Map<string, string>>(new Map<string, string>());
  const [assignmentsMap] = useState<Map<string, boolean>>(new Map<string, boolean>());
  const [assignmentsKey, setAssignmentsKey] = useState<string>('');
  const [reassignmentsMap] = useState<Map<string, string>>(new Map<string, string>());
  const [reassignmentsKey, setReassignmentsKey] = useState<string>('');
  const [reassignedMap, setReassignedMap] = useState<Map<string, ContactListItem | Contact>>(new Map<string, ContactListItem | Contact>());
  const [reassignedKey, setReassignedKey] = useState<string>('');

  useEffect(() => {
    if (contactQuery.data) {
      setContactTypes(contactQuery.data.contact_types);
      const michelinCT = hasMichelinContactTypes(contactQuery.data.contact_types, false);
      const michelinCTLevels = hasMichelinContactTypes(contactQuery.data.contact_types, true);
      setHasMichelinContactTypesLevels(!!(michelinCTLevels.length && (michelinCT.indexOf(SPPContactTypeRoleAreasEnum.pos_administrator) !== -1)));
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      if (action === 'reassign') forceReassignContactTypes(contactQuery.data.contact_types);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactQuery.data]);

  useEffect(() => {
    if (contactListQuery.data?.data?.length) {
      contactListQuery.data.data.forEach((contact:ContactListItem) => {
        if (contact.id === contactId) return;
        const contactName = `${contact.first_name} ${contact.last_name}`;
        contact.contact_types.forEach((contact_type:ContactType) => {
          const serviceName = contact_type.service;
          contact_type.role_areas.forEach((role_area:ContactTypeRole) => {
            const roleAreaName = role_area.role_area;
            role_area.levels.forEach((level:ContactTypeRoleLevels) => {
              const levelName = level.level;
              if (levelName !== SPPContactTypeLevelsEnum.general) {
                existingAssignmentsMap.set(getContactTypeMapKey(serviceName, roleAreaName, levelName), contactName);
              }
            });
          });
        });
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contactListQuery.data]);

  useEffect(() => {
    onChange(
      {
        allowClose: !saving,
        showReassign: (action === 'reassign' || hasMichelinContactTypesLevels) && !reassigning,
        allowReassign: reassignmentsKey.length > 2,
        showSave: action === 'assign' || reassigning,
        allowSave: (assignmentsKey.length > 2 && reassignmentsKey.length <= 2) || (reassignedMap.size === reassignmentsMap.size),
        startReassign: () => { setReassigning(true); },
        isSaving: saving,
        isReassigning: reassigning,
        data: [],
        saveChanges: async () => {
          setSaving(true);
          if (reassigning) {
            await saveReassignments(stNumber, reassignedMap);
            contactQuery.refetch();
            setSaving(false);
            onSave?.();
          } else if (currentlyAssignedContactsMap.size) {
            confirmDialog(
              // eslint-disable-next-line max-len
              t('You have specified priority level(s) to contact type(s) at the location where identical contact types and priority levels are currently assigned to other contact(s). Click YES to reassign the existing priority level(s) to {{contactFirstName}} {{contactLastName}} and save all priority level assignments for this contact. Click NO to go back and resolve priority level conflicts by removing selections for those levels where another contact is currently assigned.',
                {
                  contactFirstName: contactQuery.data.first_name,
                  contactLastName: contactQuery.data.last_name,
                }),
              t('Reassign existing priority levels'),
              t('Yes'),
              t('No'),
              async () => {
                await savePriorityLevels(contactId, stNumber, contactTypes);
                contactQuery.refetch();
                setSaving(false);
                onSave?.();
              },
              () => {
                setSaving(false);
              },
              { disableBackdropClick: true, disableEscapeKeyDown: true, hideCloseIcon: true },
            );
          } else {
            await savePriorityLevels(contactId, stNumber, contactTypes);
            contactQuery.refetch();
            setSaving(false);
            onSave?.();
          }
        },
      },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [saving, reassigning, assignmentsKey, reassignmentsKey, hasMichelinContactTypesLevels, reassignedKey]);

  /// //////////////////////////////////////////////////////////////////
  /// OTHER METHODS
  /// //////////////////////////////////////////////////////////////////

  function isCheckboxSelected(service: string, role_area: string, level: string) {
    if (assignmentsMap.get(getContactTypeMapKey(service, role_area, level))) return true;
    let isSelected = false;
    contactTypes.forEach((contact_type:ContactType) => {
      if (contact_type.service !== service) return;
      contact_type.role_areas.forEach((r_area:ContactTypeRole) => {
        if (r_area.role_area !== role_area) return;
        r_area.levels.forEach((lvl:ContactTypeRoleLevels) => {
          if (lvl.level === level) isSelected = true;
        });
      });
    });
    return isSelected;
  }

  function isCheckboxDisabled(service: string, roleArea: ContactTypeRole) {
    if (!contactQuery.data.contact_types) return false;

    // Disable when reassigning
    const reassignKey = `${service}~${roleArea.role_area}`;
    const reassignment = reassignmentsMap.get(reassignKey);
    if (reassignment) return true;

    const cTypes = cloneDeep(contactQuery.data.contact_types);
    // eslint-disable-next-line no-restricted-syntax
    for (const i in cTypes) {
      if (cTypes[i].service !== service) continue;
      // eslint-disable-next-line no-restricted-syntax
      for (const j in cTypes[i].role_areas) {
        if (cTypes[i].role_areas[j].role_area !== roleArea.role_area) continue;
        if ((cTypes[i].role_areas[j].role_area === 'pos_administrator')
          && (cTypes[i].role_areas[j].levels[0].level === SPPContactTypeLevelsEnum.primary
            || cTypes[i].role_areas[j].levels[0].level === SPPContactTypeLevelsEnum.secondary)) return true;
      }
    }
    return false;
  }

  function calculatePriorityAll(contactTypesParam: ContactType[]) {
    let primary:number = 0;
    let secondary:number = 0;
    let all:number = 0;

    const cTypes:ContactType[] = cloneDeep(contactTypesParam);

    cTypes.forEach((contact_type:ContactType) => {
      contact_type.role_areas.forEach((role_area:ContactTypeRole) => {
        all++;
        const level = role_area.levels.pop();
        if (level?.level === SPPContactTypeLevelsEnum.primary) primary++;
        if (level?.level === SPPContactTypeLevelsEnum.secondary) secondary++;
      });
    });

    return {
      allPrimary: (primary === all),
      allSecondary: (secondary === all),
    };
  }

  function changePriorityAll(newValue: boolean, levelToAll: SPPContactTypeLevels) {
    const cTypes:ContactType[] = cloneDeep(contactTypes);

    // eslint-disable-next-line guard-for-in,no-restricted-syntax
    for (const i in cTypes) {
      const { service } = cTypes[i];
      // eslint-disable-next-line no-restricted-syntax
      for (const j in cTypes[i].role_areas) {
        if (isCheckboxDisabled(cTypes[i].service, cTypes[i].role_areas[j])) continue;
        const { role_area } = cTypes[i].role_areas[j];
        let newLevel:SPPContactTypeLevels = SPPContactTypeLevelsEnum.general;
        if (newValue) {
          newLevel = levelToAll;
        }
        cTypes[i].role_areas[j].levels[0].level = newLevel;

        if (newValue) {
          assignmentsMap.delete(getContactTypeMapKey(service, role_area, SPPContactTypeLevelsEnum.primary));
          assignmentsMap.delete(getContactTypeMapKey(service, role_area, SPPContactTypeLevelsEnum.secondary));
          assignmentsMap.set(getContactTypeMapKey(service, role_area, levelToAll), true);
          const contactName = existingAssignmentsMap.get(getContactTypeMapKey(service, role_area, levelToAll));
          if (contactName) currentlyAssignedContactsMap.set(getContactTypeMapKey(service, role_area, levelToAll), contactName);
        } else {
          assignmentsMap.delete(getContactTypeMapKey(service, role_area, levelToAll));
          currentlyAssignedContactsMap.delete(getContactTypeMapKey(service, role_area, levelToAll));
        }

        setAssignmentsKey(JSON.stringify(Array.from(assignmentsMap.entries())));
      }
    }

    setContactTypes(cTypes);
    const allSelected = calculatePriorityAll(cTypes);

    let allPrimaryClick:boolean = false;
    let allSecondaryClick:boolean = false;
    if (levelToAll === SPPContactTypeLevelsEnum.primary) {
      allPrimaryClick = newValue;
      allSecondaryClick = false;
    }
    if (levelToAll === SPPContactTypeLevelsEnum.secondary) {
      allPrimaryClick = newValue;
      allSecondaryClick = false;
    }

    setAllPrimary(allSelected.allPrimary);
    setAllSecondary(allSelected.allSecondary);
    setAllPrimaryClicked(allPrimaryClick);
    setAllSecondaryClicked(allSecondaryClick);
  }

  function forceReassignContactTypes(contactTypesParam: ContactType[]) {
    const cTypes:ContactType[] = cloneDeep(contactTypesParam);
    // eslint-disable-next-line guard-for-in,no-restricted-syntax
    for (const i in cTypes) {
      // eslint-disable-next-line guard-for-in,no-restricted-syntax
      for (const j in cTypes[i].role_areas) {
        const hasPriorityLevel = !(cTypes[i].role_areas[j].levels[0].level === SPPContactTypeLevelsEnum.general);
        if ((hasPriorityLevel)) {
          const prevLevel = cTypes[i].role_areas[j].levels[0].level;
          const reassignKey = getContactTypeMapKey(cTypes[i].service, cTypes[i].role_areas[j].role_area);
          reassignmentsMap.set(reassignKey, prevLevel);
          setReassignmentsKey(JSON.stringify(Array.from(reassignmentsMap.entries())));
        }
      }
    }
  }

  async function changeCheckBox(service: SPPContactTypeServices, role_area: SPPContactTypeRoleAreas, newLevel: SPPContactTypeLevels, checked: boolean) {
    const cTypes:ContactType[] = cloneDeep(contactTypes);
    // eslint-disable-next-line no-restricted-syntax
    for (const i in cTypes) {
      if (cTypes[i].service !== service) continue;
      // eslint-disable-next-line no-restricted-syntax
      for (const j in cTypes[i].role_areas) {
        if (cTypes[i].role_areas[j].role_area !== role_area) continue;
        cTypes[i].role_areas[j].levels[0].level = (checked) ? newLevel : SPPContactTypeLevelsEnum.general;
      }
    }

    await setContactTypes(cTypes);

    if (checked) {
      assignmentsMap.delete(getContactTypeMapKey(service, role_area, SPPContactTypeLevelsEnum.primary));
      assignmentsMap.delete(getContactTypeMapKey(service, role_area, SPPContactTypeLevelsEnum.secondary));
      currentlyAssignedContactsMap.delete(getContactTypeMapKey(service, role_area, SPPContactTypeLevelsEnum.primary));
      currentlyAssignedContactsMap.delete(getContactTypeMapKey(service, role_area, SPPContactTypeLevelsEnum.secondary));
      assignmentsMap.set(getContactTypeMapKey(service, role_area, newLevel), true);
      const contactName = existingAssignmentsMap.get(getContactTypeMapKey(service, role_area, newLevel));
      if (contactName) currentlyAssignedContactsMap.set(getContactTypeMapKey(service, role_area, newLevel), contactName);
    } else {
      assignmentsMap.delete(getContactTypeMapKey(service, role_area, newLevel));
      currentlyAssignedContactsMap.delete(getContactTypeMapKey(service, role_area, newLevel));
    }

    setAssignmentsKey(JSON.stringify(Array.from(assignmentsMap.entries())));

    const allSelected = calculatePriorityAll(cTypes);

    if (!allSelected.allPrimary) setAllPrimaryClicked(false);
    if (!allSelected.allSecondary) setAllSecondaryClicked(false);

    setAllPrimary(allSelected.allPrimary);
    setAllSecondary(allSelected.allSecondary);
    setAllPrimaryClicked(allPrimaryClicked);
    setAllSecondaryClicked(allSecondaryClicked);
  }

  async function changeReassignCheckbox(reassignKey: string, checked: boolean) {
    const keysArray = reassignKey.split('~');
    const service = keysArray[0];
    const role_area = keysArray[1];
    if (checked) {
      // eslint-disable-next-line no-nested-ternary
      const prevLevel = isCheckboxSelected(service, role_area, SPPContactTypeLevelsEnum.primary)
        ? SPPContactTypeLevelsEnum.primary
        : (isCheckboxSelected(service, role_area, SPPContactTypeLevelsEnum.secondary)
          ? SPPContactTypeLevelsEnum.secondary
          : SPPContactTypeLevelsEnum.general
        );
      await reassignmentsMap.set(reassignKey, prevLevel);
    } else {
      await reassignmentsMap.delete(reassignKey);
    }
    setReassignmentsKey(JSON.stringify(Array.from(reassignmentsMap.entries())));
  }

  /// //////////////////////////////////////////////////////////////////
  /// RENDER COLUMNS EXTRA METHODS
  /// //////////////////////////////////////////////////////////////////

  function getCheckboxes(contactType: ContactType): JSX.Element[] {
    if (!contactType || !contactType.role_areas) return [];

    const res: JSX.Element[] = [];

    const { service } = contactType;

    contactType.role_areas.forEach((roleArea:ContactTypeRole) => {
      const { role_area } = roleArea;

      const primaryChecked = isCheckboxSelected(service, role_area, SPPContactTypeLevelsEnum.primary);
      const secondaryChecked = isCheckboxSelected(service, role_area, SPPContactTypeLevelsEnum.secondary);
      const checkboxDisabled = isCheckboxDisabled(service, roleArea);

      res.push(
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ margin: '0.5rem', fontSize: '13px', display: 'inline' }}>{t('Primary')}</span>
          <Checkbox
            color="primary"
            checked={primaryChecked}
            disabled={checkboxDisabled}
            disableFocusRipple
            disableRipple
            disableTouchRipple
            style={{ padding: 0, margin: '0.5rem', display: 'inline' }}
            onChange={(e) => changeCheckBox(service, role_area, SPPContactTypeLevelsEnum.primary, e.target.checked)}
          />
          <span style={{ margin: '0.5rem', fontSize: '13px', display: 'inline' }}>{t('Secondary')}</span>
          <Checkbox
            color="primary"
            checked={secondaryChecked}
            disabled={checkboxDisabled}
            disableFocusRipple
            disableRipple
            disableTouchRipple
            style={{ padding: 0, margin: '0.5rem', display: 'inline' }}
            onChange={(e) => changeCheckBox(service, role_area, SPPContactTypeLevelsEnum.secondary, e.target.checked)}
          />
        </div>,
      );
    });

    return res;
  }

  function getReassignCheckboxes(contactType: ContactType): JSX.Element[] {
    if (!contactType || !contactType.role_areas || !contactType.role_areas.length) return [];
    const res: JSX.Element[] = [];

    const { service } = contactType;

    const emptyCheckbox = (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        visibility: 'hidden',
        height: 40,
      }}
      >
        <Checkbox
          color="primary"
          checked={false}
          readOnly
          disabled
          disableFocusRipple
          disableRipple
          disableTouchRipple
          style={{ padding: 0, margin: '0.5rem', display: 'inline' }}
        />
      </div>
    );

    contactType.role_areas.forEach((roleArea:ContactTypeRole) => {
      // A reassign checkbox will be displayed when using the reassign action and
      // the current contact type has a priority level...
      // or when the action is assign and the current contact type
      // is a Michelin Contact Type with a priority level set

      const hasPriorityLevel = !isCheckboxSelected(service, roleArea.role_area, SPPContactTypeLevelsEnum.general);

      if (!hasPriorityLevel && action === 'assign') {
        res.push(emptyCheckbox);
        return;
      }

      const isMichelinContactType = (roleArea.role_area === SPPContactTypeRoleAreasEnum.pos_administrator);
      const displayReassign = hasPriorityLevel && ((action === 'reassign') || (action === 'assign' && isMichelinContactType));

      if (displayReassign) {
        const reassignKey = `${service}~${roleArea.role_area}`;

        const reassignMap = !!reassignmentsMap.get(reassignKey);

        const readOnly:boolean = (hasPriorityLevel && isMichelinContactType && action === 'reassign') || forceReassign;

        res.push(
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', height: 40,
          }}
          >
            <Checkbox
              color="primary"
              checked={reassignMap}
              readOnly={readOnly}
              disabled={false}
              disableFocusRipple
              disableRipple
              disableTouchRipple
              style={{ padding: 0, margin: '0.5rem', display: 'inline' }}
              onChange={!readOnly ? (e) => changeReassignCheckbox(reassignKey, e.target.checked) : undefined}
            />
          </div>,
        );
        return;
      }

      if (action === 'assign') res.push(emptyCheckbox);
    });

    return res;
  }

  function getContactTypes(contactType: ContactType): any[] {
    if (!contactType || !contactType.role_areas || !contactType.role_areas.length) return [];
    const res: any[] = [];

    contactType.role_areas.forEach(
      (roleArea) => {
        const { service } = contactType;
        const { role_area } = roleArea;
        const level:string = (roleArea.levels[0]?.level) || SPPContactTypeLevelsEnum.general;
        if (action === 'assign' || (action === 'reassign' && !isCheckboxSelected(service, role_area, SPPContactTypeLevelsEnum.general))) {
          res.push({
            service: GetServiceLabel(service),
            role: GetRoleAreaLabel(role_area),
            roleKey: role_area,
            level: GetLevelLabel(level),
          });
        }
      },
    );

    return res;
  }

  /// //////////////////////////////////////////////////////////////////
  /// RENDER TABLE HEADINGS METHODS
  /// //////////////////////////////////////////////////////////////////

  const contactTypesHeaderCellRender = () => (
    <div style={{ textAlign: 'left' }}>
      <h3 style={{ margin: 0 }}>{t('Contact Types')}</h3>
    </div>
  );

  const currentlyAssignedHeaderCellRender = () => (
    <div style={{ textAlign: 'center' }}>
      <h3 style={{ margin: 0 }}>{t('Currently Assigned')}</h3>
    </div>
  );

  const priorityHeaderCellRender = () => (
    <div style={{ textAlign: 'center' }}>
      <h3 style={{ margin: 0 }}>Priority Level</h3>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <span style={{ margin: '0.5rem', fontSize: '13px', display: 'inline' }}>{t('Primary')}</span>
        <Checkbox
          color="primary"
          disableTouchRipple
          disableRipple
          disableFocusRipple
          style={{ padding: 0, margin: '0.5rem', display: 'inline' }}
          checked={(allPrimaryClicked && !allSecondary) || allPrimary}
          onChange={(e) => { changePriorityAll(e.target.checked, SPPContactTypeLevelsEnum.primary); }}
          disabled={false}
        />
        <span style={{ margin: '0.5rem', fontSize: '13px', display: 'inline' }}>{t('Secondary')}</span>
        <Checkbox
          color="primary"
          disableTouchRipple
          disableRipple
          disableFocusRipple
          style={{ padding: 0, margin: '0.5rem', display: 'inline' }}
          checked={(allSecondaryClicked && !allPrimary) || allSecondary}
          onChange={(e) => { changePriorityAll(e.target.checked, SPPContactTypeLevelsEnum.secondary); }}
          disabled={false}
        />
      </div>
    </div>
  );

  const reassignHeaderCellRender = () => (
    <div style={{ textAlign: 'center' }}>
      <h3 style={{ margin: 0 }}>{t('Reassign')}</h3>
    </div>
  );

  /// //////////////////////////////////////////////////////////////////
  /// RENDER COLUMN METHODS
  /// //////////////////////////////////////////////////////////////////

  function renderContactTypes(contactType:ContactType):JSX.Element {
    if (!contactType.role_areas || !contactType.role_areas.length) return <div />;
    const cType = getContactTypes(contactType);
    return (
      <div>
        {cType.map((e:any) => (
          <div
            key={`${e.service}-${e.role}`}
            style={{ display: 'flex', alignItems: 'center', height: '40px' }}
          >
            <b>{e.service}</b>
            &nbsp;
            {' '}
            <span>{e.role}</span>
            &nbsp;
            {(props.action === 'reassign') ? (`[${e.level}]`) : null}
          </div>
        ))}
      </div>
    );
  }

  function renderPriorityLevels(contactType:ContactType):JSX.Element {
    if (!contactType.role_areas || !contactType.role_areas.length) return <div />;
    const checkboxes = getCheckboxes(contactType);
    return (
      <div>
        {/* eslint-disable-next-line react/no-array-index-key */}
        {checkboxes.map((e:JSX.Element, index:number) => <div key={`index-priority-${index}`}>{e}</div>)}
      </div>
    );
  }

  function renderReassign(contactType:ContactType):JSX.Element {
    if (!contactType.role_areas || !contactType.role_areas.length) return <div />;
    const checkboxes = getReassignCheckboxes(contactType);
    return (
      <div>
        {/* eslint-disable-next-line react/no-array-index-key */}
        {checkboxes.map((e:JSX.Element, index:number) => <div key={`index-priority-reassign-${index}`}>{e}</div>)}
      </div>
    );
  }

  function getContactsAssigned(contactType: ContactType):string[] {
    if (!contactType || !contactType.role_areas || !contactType.role_areas.length) return [];

    const res:string[] = [];

    const { service } = contactType;

    contactType.role_areas.forEach((roleArea: ContactTypeRole) => {
      const { role_area } = roleArea;
      const level = (roleArea.levels[0]?.level) || SPPContactTypeLevelsEnum.general;
      const contactName = currentlyAssignedContactsMap.get(getContactTypeMapKey(service, role_area, level));
      res.push(contactName || '');
    });

    return res;
  }

  function renderCurrentlyAssigned(contactType:ContactType):JSX.Element {
    if (!contactType.role_areas || !contactType.role_areas.length) return <div />;
    const contactNames = getContactsAssigned(contactType);
    return (
      <div>
        {contactNames.map((name:string, index:number) => (
          <div
            /* eslint-disable-next-line react/no-array-index-key */
            key={`${name}~${index}`}
            style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center', height: '40px',
            }}
          >
            {name}
          </div>
        ))}
      </div>
    );
  }

  /// //////////////////////////////////////////////////////////////////
  /// RENDER PRIORITY LEVELS TABLE
  /// //////////////////////////////////////////////////////////////////

  function renderPriorityLevelsTable():JSX.Element {
    const tableData = (contactTypes || []).filter((ct: { role_areas: string | any[]; }) => (ct.role_areas?.length));

    return (
      <DataGrid
        dataSource={tableData}
        allowColumnReordering
        allowColumnResizing
        keyExpr="id"
        style={{ maxHeight: 'max(30vh, calc(100vh - 352px))' }}
        noDataText={t('There are currently no contact types for this contact.')}
      >
        <FilterRow visible={false} />
        <SearchPanel visible={false} highlightCaseSensitive={false} />
        {action === 'reassign' || hasMichelinContactTypesLevels
          ? (
            <Column
              width={100}
              caption={t('Reassign priority levels')}
              headerCellRender={reassignHeaderCellRender}
              cellRender={({ row }) => renderReassign(row.data)}
            />
          )
          : null}
        <Column
          caption={t('Contact Types')}
          headerCellRender={contactTypesHeaderCellRender}
          cellRender={({ row }) => renderContactTypes(row.data)}
        />
        {props.action === 'assign'
          ? (
            <Column
              caption={t('Priority Level')}
              headerCellRender={priorityHeaderCellRender}
              cellRender={({ row }) => renderPriorityLevels(row.data)}
            />
          )
          : null}
        {props.action === 'assign'
          ? (
            <Column
              width={350}
              caption={t('Currently Assigned')}
              headerCellRender={currentlyAssignedHeaderCellRender}
              cellRender={({ row }) => renderCurrentlyAssigned(row.data)}
            />
          )
          : null}
      </DataGrid>

    );
  }

  const renderInfo = () => {
    // eslint-disable-next-line max-len
    let title = t('For this location, specify if the contact will be a primary or secondary level point of contact for each contact type. Leave check boxes empty if a priority level is not required or if it is currently assigned to another contact at the location.');

    if (action === 'reassign') {
      title = (reassigning)
        ? t(
          // eslint-disable-next-line max-len
          'For each contact type shown below, select another contact at the location for which you wish to reassign {{contactFirstName}} {{contactLastName}}’s contact types and associated priority levels. Clicking on the top of each accordion panel will expand or collapse the contents for each contact type currently assigned to {{contactFirstName}} {{contactLastName}}. Save your changes once all reassignments have been completed.',
          { contactFirstName: contactQuery.data.first_name, contactLastName: contactQuery.data.last_name },
        ) : t(
          'For each contact type shown below, select the ones you wish to reassign.',
          { contactFirstName: contactQuery.data.first_name, contactLastName: contactQuery.data.last_name },
        );
    }

    const accName:string = customerQuery.data.customer_dba_name || customerQuery.data.customer_name || '';
    const accNo:string = stNumber;
    const storeNo:string = customerQuery.data.extrnl_cust_id || '';

    return (
      <>
        <Grid item xs={12}>
          {title}
        </Grid>
        <Grid item xs={12}>
          <b>Account Name:</b>
          {' '}
          {accName}
          <br />
          <b>Account #:</b>
          {' '}
          {accNo}
          <br />
          <b>Store #:</b>
          {' '}
          {storeNo}
        </Grid>
      </>
    );
  };

  return (
    <>
      {useMemo(() => (
        (contactQuery.isLoading || customerQuery.isLoading || contactListQuery.isLoading)
          ? <LoadingBounce style={{ height: '70vh' }} />
          : (
            <Grid container spacing={2}>
              {renderInfo()}
              <Grid item xs={12}>
                {reassigning
                  ? (
                    <PriorityLevelsReassignment
                      stNumber={stNumber}
                      btNumber={btNumber}
                      countryCode={countryCode}
                      reassignedContact={contactQuery.data}
                      reassignmentsMap={reassignmentsMap}
                      onChange={(reassigned: Map<string, ContactListItem | Contact>) => {
                        setReassignedMap(reassigned);
                        setReassignedKey(JSON.stringify(Array.from(reassigned.entries())));
                      }}
                    />
                  )
                  : renderPriorityLevelsTable()}
              </Grid>
            </Grid>
          )
      ),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [contactQuery.isLoading,
        customerQuery.isLoading,
        contactListQuery.isLoading,
        assignmentsKey,
        reassignmentsKey,
        contactTypes,
        saving,
        reassigning,
        allPrimary,
        allSecondary,
      ])}
    </>
  );
}

export {
  PriorityLevelsManagement,
};
